import { emailRegex } from "@/src/utils/regex";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "../../atom/Button";
import Dialog from "../../atom/Dialog";
import DialogFooterContainer from "../../atom/Dialog/DialogFooterContainer";
import MultipleInput from "../../atom/Input/MultipleInput";
import Label from "../../atom/Label";
import Typo from "../../atom/Typo";
import CallOut from "../../molecule/CallOut";
import SectionCardRow from "../../molecule/SectionCardRow";

interface SendLinkCodeDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  linkCode: string;
  onOk: (emailList: string[]) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
  codeLabel?: string;
  initialEmails?: string[];
}

function SendLinkCodeDialog({
  open,
  onOpenChange,
  linkCode,
  onOk,
  isLoading,
  title,
  description,
  codeLabel,
  initialEmails,
}: SendLinkCodeDialogProps) {
  const { t } = useTranslation();

  const {
    control,
    watch,
    setValue,
    setFocus,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<{
    email: string;
    emailList: string[];
  }>({
    mode: "onBlur",
    defaultValues: {
      email: "",
      emailList: initialEmails || [],
    },
  });

  const validationEmail = (value: string) => {
    return emailRegex.test(value);
  };

  const filterUniqueStrings = (strings: string[]) => {
    return strings.filter((value, index, self) => {
      return self.indexOf(value) === index;
    });
  };

  useEffect(() => {
    setFocus("email");
  }, [setFocus]);

  return (
    <Dialog
      open={open}
      title={title || t("common:sendClientInviteCode")}
      onOpenChange={onOpenChange}
      width={640}
      footer={
        <DialogFooterContainer>
          <Button
            buttonGrade="tertiary"
            buttonColor="black"
            onClick={() => onOpenChange(false)}
          >
            <Typo typoType="btn3m">{t("common:cancel")}</Typo>
          </Button>
          <Button
            onClick={() =>
              onOk(
                getValues("emailList").filter((email) =>
                  validationEmail(email),
                ),
              )
            }
            disabled={
              watch("emailList").filter((email) => validationEmail(email))
                .length === 0 || isLoading
            }
            isLoading={isLoading}
          >
            <Typo typoType="btn3m" color="white">
              {t("common:send")}
            </Typo>
          </Button>
        </DialogFooterContainer>
      }
    >
      <FlexColumn>
        <DescriptionContainer>
          {(description || t("common:sendClientInviteCodeGuide"))
            .split("\n")
            .map((line, index) => (
              <Typo key={index} typoType="b7m">
                {line}
              </Typo>
            ))}
        </DescriptionContainer>

        <SectionCardRow
          label={codeLabel || t("common:linkCode")}
          direction="vertical"
          value={
            <StyledCallOut backgroundColor="white">
              <Typo typoType="d5" color="blue4">
                {linkCode}
              </Typo>
            </StyledCallOut>
          }
        />

        <SectionCardRow
          label={<Label required>{t("common:email")}</Label>}
          direction="vertical"
          value={
            <Controller
              control={control}
              name="email"
              rules={{ required: true }}
              render={({ field }) => (
                <MultipleInput
                  {...field}
                  placeholder={t("placeholder:email")}
                  valueList={watch("emailList")}
                  onChangeValue={(value) => setValue("email", value)}
                  onChangeValueList={(valueList) => {
                    const uniqueEmailList = filterUniqueStrings(valueList);

                    if (uniqueEmailList.length >= 1) {
                      clearErrors("emailList");
                    }
                    return setValue("emailList", uniqueEmailList);
                  }}
                  onBlur={() => {
                    const isNoUniqueEmails =
                      filterUniqueStrings(watch("emailList")).length === 0;

                    isNoUniqueEmails &&
                      setError("emailList", { type: "require" });
                  }}
                  validation={validationEmail}
                  isError={!!errors.emailList}
                  errorMessage={t("error:required")}
                />
              )}
            />
          }
        />
      </FlexColumn>
    </Dialog>
  );
}

export default SendLinkCodeDialog;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
`;

const StyledCallOut = styled(CallOut)`
  padding: 12px 16px;
  justify-content: center;
`;
